import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Avatar,
  Empty,
  Spin,
  Input,
  Button,
  message as antdMessage,
  Dropdown,
} from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  CloseCircleFilled,
  MoreOutlined,
} from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import {
  getWhatsappMessagesByPhone,
  sendWhatsappImageFromCRM,
  sendWhatsappMessageFromCRM,
  updateLeadStatus,
} from "../features/customers/customerSlice";
import { base_url } from "../utils/baseUrl";
import "../styles/agentWhatsapp.css";

const { Title } = Typography;
const { TextArea } = Input;

const FOLLOW_UP_TEMPLATE_MESSAGE =
  "Hello, we are following up on your earlier inquiry. Please reply here if you would like us to continue assisting you.";

const MAX_IMAGES = 20;

const AgentWhatsappChatPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { phone } = useParams();
  const isDesktop = useMediaQuery({ minWidth: 785 });

  const { whatsappMessages, isLoading, leads = [] } = useSelector(
    (state) => state.customer
  );

  const [newMessage, setNewMessage] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSendingImage, setIsSendingImage] = useState(false);

  const fileInputRef = useRef(null);

  const normalizedPhone = useMemo(() => {
    return phone ? decodeURIComponent(phone) : "";
  }, [phone]);

  useEffect(() => {
    if (normalizedPhone) {
      dispatch(getWhatsappMessagesByPhone(normalizedPhone));
    }
  }, [dispatch, normalizedPhone]);

  const currentLead = useMemo(() => {
    return leads.find((lead) => lead.phone === normalizedPhone);
  }, [leads, normalizedPhone]);

  const customerName = currentLead?.name || "Unknown";

  const lastIncomingCustomerMessage = useMemo(() => {
    return [...(whatsappMessages || [])]
      .filter(
        (msg) => msg.direction === "incoming" && msg.category === "customer"
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  }, [whatsappMessages]);

  const isSessionActive = useMemo(() => {
    if (!lastIncomingCustomerMessage?.createdAt) return false;

    const lastIncomingTime = new Date(
      lastIncomingCustomerMessage.createdAt
    ).getTime();
    const now = Date.now();
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

    return now - lastIncomingTime <= TWENTY_FOUR_HOURS;
  }, [lastIncomingCustomerMessage]);

  useEffect(() => {
    if (isSessionActive) {
      setNewMessage("");
    } else {
      setNewMessage(FOLLOW_UP_TEMPLATE_MESSAGE);
    }
    clearAllImages();
  }, [isSessionActive, normalizedPhone]);

  const getInitials = (name, phoneValue) => {
    if (name && name.trim()) {
      const parts = name.trim().split(" ");
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (
        parts[0].charAt(0).toUpperCase() +
        parts[1].charAt(0).toUpperCase()
      );
    }
    return phoneValue?.slice(-2) || "?";
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleString();
  };

  const handleBack = () => {
    navigate("/admin/my-leads");
  };

  const handlePickImage = () => {
    if (!isSessionActive) {
      antdMessage.warning(
        "Session expired. Image messages are disabled until the customer replies."
      );
      return;
    }

    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (allowedTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length) {
      antdMessage.error(
        `Unsupported files skipped: ${invalidFiles.join(", ")}`
      );
    }

    const remainingSlots = MAX_IMAGES - selectedImages.length;

    if (remainingSlots <= 0) {
      antdMessage.warning(`You can only upload up to ${MAX_IMAGES} images`);
      e.target.value = "";
      return;
    }

    const filesToAdd = validFiles
      .slice(0, remainingSlots)
      .map((file, index) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${index}`,
        file,
        preview: URL.createObjectURL(file),
      }));

    if (validFiles.length > remainingSlots) {
      antdMessage.warning(
        `Only ${remainingSlots} more image(s) can be added`
      );
    }

    setSelectedImages((prev) => [...prev, ...filesToAdd]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const clearAllImages = () => {
    setSelectedImages((prev) => {
      prev.forEach((item) => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
      return [];
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeSelectedImage = (id) => {
    setSelectedImages((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.preview) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleSendMessage = async () => {
    if (!normalizedPhone) {
      antdMessage.warning("No phone number found");
      return;
    }

    if (!newMessage.trim()) {
      antdMessage.warning(
        isSessionActive ? "Type a message first" : "No template message found"
      );
      return;
    }

    try {
      const result = await dispatch(
        sendWhatsappMessageFromCRM({
          phone: normalizedPhone,
          message: newMessage.trim(),
        })
      ).unwrap();

      if (result?.success === false) {
        throw new Error(
          result?.error || result?.message || "Failed to send message"
        );
      }

      if (currentLead?._id && currentLead?.status === "new") {
        await dispatch(
          updateLeadStatus({
            id: currentLead._id,
            status: "contacted",
          })
        ).unwrap();
      }

      if (isSessionActive) {
        setNewMessage("");
      } else {
        setNewMessage(FOLLOW_UP_TEMPLATE_MESSAGE);
      }

      antdMessage.success(
        result?.sentWith === "template"
          ? "Session expired. Follow-up template sent"
          : "Message sent"
      );

      await dispatch(getWhatsappMessagesByPhone(normalizedPhone)).unwrap();
    } catch (error) {
      console.error("Send message error:", error);
      antdMessage.error(
        error?.error ||
          error?.message ||
          error?.details?.error ||
          "Failed to send message"
      );
    }
  };

  const handleSendImages = async () => {
    if (!normalizedPhone) {
      antdMessage.warning("No phone number found");
      return;
    }

    if (!isSessionActive) {
      antdMessage.warning(
        "Session expired. You can only send images after the customer replies."
      );
      return;
    }

    if (!selectedImages.length) {
      antdMessage.warning("Choose image(s) first");
      return;
    }

    try {
      setIsSendingImage(true);

      const formData = new FormData();
      formData.append("phone", normalizedPhone);
      formData.append("caption", newMessage.trim());

      selectedImages.forEach((item) => {
        formData.append("images", item.file);
      });

      const result = await dispatch(sendWhatsappImageFromCRM(formData)).unwrap();

      if (result?.success === false || !result?.data?.length) {
        throw new Error(
          result?.error ||
            result?.message ||
            "No images were sent successfully"
        );
      }

      if (currentLead?._id && currentLead?.status === "new") {
        await dispatch(
          updateLeadStatus({
            id: currentLead._id,
            status: "contacted",
          })
        ).unwrap();
      }

      const actualSentCount = result?.data?.length || 0;

      clearAllImages();
      setNewMessage("");
      antdMessage.success(`${actualSentCount} image(s) sent successfully`);

      await dispatch(getWhatsappMessagesByPhone(normalizedPhone)).unwrap();
    } catch (error) {
      console.error("Send image error:", error);
      antdMessage.error(
        error?.error ||
          error?.message ||
          error?.details?.error ||
          "Failed to send images"
      );
    } finally {
      setIsSendingImage(false);
    }
  };

  const getMediaUrl = (item) =>
    `${base_url}crm/whatsapp/media/${item.mediaId}`;

  const getDownloadUrl = (item) =>
    `${base_url}crm/whatsapp/media/${item.mediaId}?download=1&filename=${encodeURIComponent(
      item.mediaFilename || "file"
    )}`;

const normalizeHref = (value = "") => {
  const trimmed = String(value).trim();
  if (!trimmed) return "#";
  if (/^(https?:\/\/|tel:)/i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
};

const normalizePhoneForTel = (value = "") => {
  const digits = String(value).replace(/\D/g, "");

  if (/^0[17]\d{8}$/.test(digits)) {
    return `+254${digits.slice(1)}`;
  }

  if (/^254[17]\d{8}$/.test(digits)) {
    return `+${digits}`;
  }

  return "";
};

const isKenyanPhoneLike = (value = "") => {
  const normalized = String(value).trim();

  return (
    /^0[17]\d{8}$/.test(normalized.replace(/[^\d]/g, "")) ||
    /^254[17]\d{8}$/.test(normalized.replace(/[^\d]/g, "")) ||
    /^\+254[17]\d{8}$/.test(normalized.replace(/[\s().-]/g, ""))
  );
};

const renderTextWithLinks = (text = "") => {
  if (!text) return "No text";

  const regex =
    /(\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+|\+254[\d\s().-]{9,}|254[\d\s().-]{9,}|0[\d\s().-]{9,})/gi;

  const parts = String(text).split(regex);

  return parts.map((part, index) => {
    if (!part) return null;

    const trimmed = part.trim();

    if (/^(https?:\/\/|www\.)/i.test(trimmed)) {
      return (
        <a
          key={`${trimmed}-${index}`}
          href={normalizeHref(trimmed)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#1677ff",
            textDecoration: "underline",
            wordBreak: "break-word",
          }}
        >
          {part}
        </a>
      );
    }

    if (isKenyanPhoneLike(trimmed)) {
      const telValue = normalizePhoneForTel(trimmed);

      if (telValue) {
        return (
          <a
            key={`${trimmed}-${index}`}
            href={`tel:${telValue}`}
            style={{
              color: "#1677ff",
              textDecoration: "underline",
              wordBreak: "break-word",
            }}
          >
            {part}
          </a>
        );
      }
    }

    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

  const renderMediaMenu = (item) => {
    if (!item?.mediaId) return null;

    const items = [
      {
        key: "open",
        label: (
          <a
            href={getMediaUrl(item)}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Open
          </a>
        ),
      },
      {
        key: "download",
        label: (
          <a
            href={getDownloadUrl(item)}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Download
          </a>
        ),
      },
    ];

    return (
      <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
        <Button
          type="text"
          icon={<MoreOutlined />}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 30,
            height: 30,
            minWidth: 30,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#111b21",
          }}
        />
      </Dropdown>
    );
  };

  const renderMessageContent = (item) => {
    if (item.type === "image") {
      return (
        <div>
          {item.mediaId ? (
            <div style={{ position: "relative", display: "inline-block" }}>
              <img
                src={getMediaUrl(item)}
                alt={item.mediaFilename || "WhatsApp image"}
                style={{
                  width: "100%",
                  maxWidth: 260,
                  borderRadius: 10,
                  display: "block",
                  marginBottom: item.text ? 8 : 0,
                }}
              />
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                maxWidth: 260,
                minHeight: 120,
                borderRadius: 10,
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#667781",
                fontSize: 13,
                marginBottom: item.text ? 8 : 0,
              }}
            >
              Image unavailable
            </div>
          )}

          {item.text ? (
            <div
              style={{
                fontSize: 14,
                color: "#111b21",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderTextWithLinks(item.text)}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "document") {
      return (
        <div>
          {item.mediaId ? (
            <div
              style={{
                position: "relative",
                padding: "10px 44px 10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#111b21",
                fontWeight: 500,
                marginBottom: item.text ? 8 : 0,
              }}
            >
              📄 {item.mediaFilename || "Document"}
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#667781",
                marginBottom: item.text ? 8 : 0,
              }}
            >
              📄 Document unavailable
            </div>
          )}

          {item.text ? (
            <div
              style={{
                fontSize: 14,
                color: "#111b21",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderTextWithLinks(item.text)}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "video") {
      return (
        <div>
          {item.mediaId ? (
            <div
              style={{
                position: "relative",
                padding: "10px 44px 10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#111b21",
                fontWeight: 500,
                marginBottom: item.text ? 8 : 0,
              }}
            >
              🎥 {item.mediaFilename || "Video"}
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#667781",
                marginBottom: item.text ? 8 : 0,
              }}
            >
              🎥 Video unavailable
            </div>
          )}

          {item.text ? (
            <div
              style={{
                fontSize: 14,
                color: "#111b21",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderTextWithLinks(item.text)}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "audio") {
      return (
        <div>
          {item.mediaId ? (
            <div
              style={{
                position: "relative",
                padding: "10px 44px 10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#111b21",
                fontWeight: 500,
              }}
            >
              🎵 Audio
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#667781",
              }}
            >
              🎵 Audio unavailable
            </div>
          )}
        </div>
      );
    }

    if (item.type === "sticker") {
      return (
        <div>
          {item.mediaId ? (
            <div style={{ position: "relative", display: "inline-block" }}>
              <img
                src={getMediaUrl(item)}
                alt="Sticker"
                style={{
                  width: 120,
                  maxWidth: "100%",
                  borderRadius: 10,
                  display: "block",
                }}
              />
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#667781",
              }}
            >
              Sticker unavailable
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        style={{
          fontSize: 14,
          color: "#111b21",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {renderTextWithLinks(item.text || "")}
      </div>
    );
  };

  return (
    <div className="agent__whatsapp-wrapper">
      {isDesktop && (
        <div
          style={{
            marginBottom: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Client Conversation
            </Title>
          </div>

          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Back
          </Button>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          height: isDesktop ? "calc(100vh - 180px)" : "calc(100vh - 190px)",
          border: isDesktop ? "1px solid #d9d9d9" : "none",
          borderRadius: isDesktop ? 14 : 0,
          overflow: "hidden",
          background: "#fff",
          boxShadow: isDesktop ? "0 8px 24px rgba(0,0,0,0.06)" : "none",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            background: "#efeae2",
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: isDesktop ? "14px 18px" : "12px 14px",
              borderBottom: "1px solid #d9d9d9",
              background: "#f8f9fb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {!isDesktop && (
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                  style={{
                    marginRight: -6,
                    width: 34,
                    height: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              )}

              <Avatar style={{ backgroundColor: "#128c7e" }} size={42}>
                {getInitials(customerName, normalizedPhone)}
              </Avatar>

              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  {customerName}
                </div>
                <div style={{ fontSize: 12, color: "#667781" }}>
                  {normalizedPhone}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              padding: isDesktop ? 18 : 12,
            }}
          >
            {isLoading ? (
              <div style={{ textAlign: "center", paddingTop: 50 }}>
                <Spin />
              </div>
            ) : (whatsappMessages || []).length > 0 ? (
              [...whatsappMessages]
                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                .map((item, index) => (
                  <div
                    key={item._id || index}
                    style={{
                      display: "flex",
                      justifyContent:
                        item.direction === "outgoing"
                          ? "flex-end"
                          : "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        background:
                          item.direction === "outgoing" ? "#d9fdd3" : "#fff",
                        padding: "10px 12px 8px",
                        borderRadius: 12,
                        maxWidth: isDesktop ? "72%" : "84%",
                        boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                        wordBreak: "break-word",
                      }}
                    >
                      {renderMessageContent(item)}

                      <div
                        style={{
                          marginTop: 5,
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 6,
                          fontSize: 11,
                          color: "#667781",
                          flexWrap: "wrap",
                        }}
                      >
                        <span>{formatDateTime(item.createdAt)}</span>
                        <span>{item.status || "sent"}</span>
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <Empty description="No messages found" />
            )}
          </div>

          {!isSessionActive && (
            <div
              style={{
                padding: isDesktop ? "10px 14px" : "10px 12px",
                background: "#fff7e6",
                borderTop: "1px solid #d9d9d9",
                borderBottom: "1px solid #d9d9d9",
                color: "#d46b08",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Session expired: a follow-up template will be sent instead of a
              normal message.
            </div>
          )}

          {selectedImages.length > 0 && isSessionActive && (
            <div
              style={{
                padding: isDesktop ? "12px 14px" : "10px 12px",
                borderTop: "1px solid #d9d9d9",
                background: "#f8f9fb",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                flexShrink: 0,
                maxHeight: isDesktop ? 320 : 360,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  flexShrink: 0,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, color: "#111b21" }}>
                  {selectedImages.length} image(s) selected
                </div>

                <Button
                  danger
                  type="text"
                  onClick={clearAllImages}
                  style={{ paddingInline: 0 }}
                >
                  Clear all
                </Button>
              </div>

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: 4,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isDesktop
                      ? "repeat(auto-fill, minmax(170px, 1fr))"
                      : "repeat(2, minmax(0, 1fr))",
                    gap: 12,
                  }}
                >
                  {selectedImages.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        background: "#fff",
                        border: "1px solid #d9d9d9",
                        borderRadius: 14,
                        overflow: "hidden",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          aspectRatio: "1 / 1",
                          background: "#f5f5f5",
                        }}
                      >
                        <img
                          src={item.preview}
                          alt={item.file.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />

                        <Button
                          type="text"
                          icon={
                            <CloseCircleFilled
                              style={{ color: "#ff4d4f", fontSize: 18 }}
                            />
                          }
                          onClick={() => removeSelectedImage(item.id)}
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            background: "rgba(255,255,255,0.9)",
                            borderRadius: "50%",
                            width: 30,
                            height: 30,
                            minWidth: 30,
                          }}
                        />
                      </div>

                      <div
                        style={{
                          padding: 10,
                          fontSize: 12,
                          color: "#667781",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.file.name}
                      >
                        {item.file.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-end",
                  flexWrap: isDesktop ? "nowrap" : "wrap",
                  flexShrink: 0,
                  borderTop: "1px solid #e8e8e8",
                  paddingTop: 10,
                  background: "#f8f9fb",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextArea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Add one caption for all selected images"
                    autoSize={{ minRows: 2, maxRows: 4 }}
                  />
                </div>

                <Button
                  type="primary"
                  onClick={handleSendImages}
                  loading={isSendingImage}
                  icon={<FaWhatsapp />}
                  style={{
                    background: "#25d366",
                    borderColor: "#25d366",
                    color: "#fff",
                    borderRadius: 10,
                    fontWeight: 600,
                    height: 42,
                    minWidth: isDesktop ? 150 : "100%",
                    flexShrink: 0,
                  }}
                >
                  Send {selectedImages.length} Image
                  {selectedImages.length > 1 ? "s" : ""}
                </Button>
              </div>
            </div>
          )}

          <div
            style={{
              padding: isDesktop ? 14 : 10,
              borderTop: "1px solid #d9d9d9",
              background: "#f8f9fb",
              display: "flex",
              gap: 10,
              alignItems: "flex-end",
              flexShrink: 0,
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              multiple
              style={{ display: "none" }}
              onChange={handleImageChange}
            />

            {isSessionActive && (
              <Button
                type="text"
                onClick={handlePickImage}
                style={{
                  width: 42,
                  height: 42,
                  minWidth: 42,
                  borderRadius: "50%",
                  border: "1px solid #d9d9d9",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#128c7e",
                  flexShrink: 0,
                }}
                icon={<PlusOutlined style={{ fontSize: 18 }} />}
              />
            )}

            <div style={{ flex: 1 }}>
              <TextArea
                rows={2}
                disabled={!isSessionActive || selectedImages.length > 0}
                placeholder={
                  !isSessionActive
                    ? "Session expired. A follow-up template will be sent"
                    : selectedImages.length > 0
                    ? "Use the caption box above for all selected images"
                    : "Type a message to the customer"
                }
                value={selectedImages.length > 0 ? "" : newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    if (selectedImages.length > 0 && isSessionActive) {
                      handleSendImages();
                    } else {
                      handleSendMessage();
                    }
                  }
                }}
              />
            </div>

            <Button
              type="primary"
              onClick={
                selectedImages.length > 0 && isSessionActive
                  ? handleSendImages
                  : handleSendMessage
              }
              loading={selectedImages.length > 0 ? isSendingImage : isLoading}
              disabled={
                selectedImages.length > 0
                  ? !isSessionActive
                  : !newMessage.trim()
              }
              style={{
                background: "#128c7e",
                borderColor: "#128c7e",
                color: "#fff",
                height: 42,
                minWidth: isDesktop ? 120 : 96,
                borderRadius: 8,
                flexShrink: 0,
              }}
              icon={
                selectedImages.length > 0 && isSessionActive ? <FaWhatsapp /> : null
              }
            >
              {selectedImages.length > 0 && isSessionActive
                ? `Send ${selectedImages.length}`
                : isSessionActive
                ? "Send"
                : "Send Template"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentWhatsappChatPage;